import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Modal from '../../../components/ui/Modal';
import Input from '../../../components/ui/Input';

const DataManagementTab = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleExportData = async (type) => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    // Simulate export operation
    await new Promise((resolve) => setTimeout(resolve, 1500));

    setMessage({ type: 'success', text: `${type} exported successfully. Check your email for the download link.` });
    setLoading(false);
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      setMessage({ type: 'error', text: 'Please type DELETE to confirm' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    // Simulate delete operation
    await new Promise((resolve) => setTimeout(resolve, 1500));

    setMessage({ type: 'success', text: 'Account deletion request submitted. You will receive a confirmation email.' });
    setShowDeleteModal(false);
    setDeleteConfirmation('');
    setLoading(false);
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>
          Data Management
        </h2>
        <p className="text-sm text-muted-foreground mb-6">
          Export your data or delete your account
        </p>
      </div>

      {message?.text && (
        <div
          className={`p-4 rounded-lg ${
            message?.type === 'success' ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive'
          }`}
        >
          {message?.text}
        </div>
      )}

      {/* Export Data */}
      <div>
        <h3 className="text-lg font-medium mb-4" style={{ color: 'var(--color-text-primary)' }}>
          Export Your Data
        </h3>
        <div className="space-y-4">
          <div className="p-6 rounded-lg border" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-background)' }}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                  Trip History
                </h4>
                <p className="text-sm text-muted-foreground">
                  Download all your trip data including meal plans and inventory logs
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => handleExportData('Trip History')}
                loading={loading}
              >
                Export
              </Button>
            </div>
          </div>

          <div className="p-6 rounded-lg border" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-background)' }}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                  Account Data
                </h4>
                <p className="text-sm text-muted-foreground">
                  Download all your personal account information and settings
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => handleExportData('Account Data')}
                loading={loading}
              >
                Export
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Account */}
      <div>
        <h3 className="text-lg font-medium mb-4" style={{ color: 'var(--color-text-primary)' }}>
          Delete Account
        </h3>
        <div className="p-6 rounded-lg border border-destructive/50" style={{ backgroundColor: 'var(--color-background)' }}>
          <div className="space-y-4">
            <div>
              <h4 className="font-medium text-destructive mb-1">
                Permanently Delete Your Account
              </h4>
              <p className="text-sm text-muted-foreground">
                Once you delete your account, there is no going back. All your data will be permanently removed.
              </p>
            </div>
            <Button
              variant="destructive"
              onClick={() => setShowDeleteModal(true)}
            >
              Delete Account
            </Button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteConfirmation('');
          setMessage({ type: '', text: '' });
        }}
        title="Delete Account"
      >
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-destructive/10">
            <p className="text-sm text-destructive font-medium">
              Warning: This action cannot be undone!
            </p>
          </div>
          <p className="text-sm text-muted-foreground">
            All your trips, meal plans, inventory data, and account information will be permanently deleted.
          </p>
          <Input
            type="text"
            value={deleteConfirmation}
            onChange={(e) => setDeleteConfirmation(e?.target?.value)}
            label="Type DELETE to confirm"
            placeholder="DELETE"
          />
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteModal(false);
                setDeleteConfirmation('');
              }}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              loading={loading}
              disabled={deleteConfirmation !== 'DELETE'}
              fullWidth
            >
              Delete Account
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DataManagementTab;